import React, { useCallback, useMemo, useState } from 'react';

import { useConfig } from '../../../defaultConfig';
import useEFakturaAccountNumber from '../../../fetchHooks/useEFakturaAccount';
import useFavoriteAccount from '../../../fetchHooks/useFavoriteAccount';
import { Account, AccountNumber } from '../../../generated-types';

export type UseSelectedDebitAccountType = [
    AccountNumber | undefined,
    (value: AccountNumber | undefined) => void,
    Account | undefined,
    () => void,
];

const useSelectedDebitAccount = (
    accounts: Account[],
    setError: React.Dispatch<string | undefined>,
): UseSelectedDebitAccountType => {
    const [debitAccount, setDebitAccountState] = useState<AccountNumber | undefined>();

    const setDebitAccount = useCallback(
        (value: AccountNumber | undefined) => {
            setError(undefined);
            setDebitAccountState(value);
        },
        [setError, setDebitAccountState],
    );

    const {
        featureToggles: { enableEFakturaAccount },
    } = useConfig();
    const { data: eFakturaAccountNumber, isSuccess: eFakturaAccountNumberSuccess } = useEFakturaAccountNumber({
        enabled: enableEFakturaAccount,
        onSuccess: setDebitAccount,
    });
    const { data: favoriteAccountNumber } = useFavoriteAccount({
        enabled:
            !enableEFakturaAccount || (enableEFakturaAccount && eFakturaAccountNumberSuccess && !eFakturaAccountNumber),
        onSuccess: setDebitAccount,
    });

    const targetFromAccount = useMemo(
        () => accounts.find((account) => account.accountNumber.value === debitAccount?.value),
        [debitAccount, accounts],
    );

    const resetDebitAccount = useCallback(() => {
        setDebitAccountState(eFakturaAccountNumber ?? favoriteAccountNumber);
    }, [eFakturaAccountNumber, favoriteAccountNumber]);

    return [debitAccount, setDebitAccount, targetFromAccount, resetDebitAccount];
};

export default useSelectedDebitAccount;
